import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import { TypeFilter } from '~/components/Filters/TypeFilter/TypeFilter';
import { useTypeFilterUpdate } from '~/components/Filters/TypeFilter/useTypeFilterUpdate';
import { type ItemTypeFilter } from '~/components/Filters/types';
import { clearItemTypeFiltersAction, removeFilterAction } from '~/store/filters/actions';
import { selectedAssetFileKindFiltersSelector, selectedItemTypesFiltersSelector } from '~/store/filters/selectors';
import { type SelectedFilter } from '~/store/filters/types';

export type TypeFilterCardProps = {
  availableTypes?: ItemTypeFilter[];
} & BaseFilterCardProps;

export const TypeFilterCard = memo(({ availableTypes, ...props }: TypeFilterCardProps) => {
  const dispatch = useDispatch();
  const selectedTypes = useSelector(selectedItemTypesFiltersSelector);
  const selectedAssetFileKinds = useSelector(selectedAssetFileKindFiltersSelector);
  const { onTypeSelectedChange, onAssetFileKindSelectedChange } = useTypeFilterUpdate();

  const clearValues = useCallback(() => {
    dispatch(clearItemTypeFiltersAction());
  }, [dispatch]);

  const onRemoveFilter = useCallback(
    (filter: SelectedFilter) => {
      dispatch(removeFilterAction({ filter }));
      clearValues();
    },
    [dispatch, clearValues],
  );

  return (
    <FilterCard currentFilter="type" onClearClick={clearValues} onRemoveFilter={onRemoveFilter} {...props}>
      <TypeFilter
        availableTypes={availableTypes}
        onTypeSelectedChange={onTypeSelectedChange}
        selectedTypes={selectedTypes}
        onAssetFileKindSelectedChange={onAssetFileKindSelectedChange}
        selectedAssetFileKinds={selectedAssetFileKinds}
      />
    </FilterCard>
  );
});

TypeFilterCard.displayName = 'TypeFilterCard';
